import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ExternalLink, Download, Printer, Loader2, Sparkles } from 'lucide-react';
import { getAiProviderArgs } from '../lib/aiProviderPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ──────────────────────────────────────────────────────────────────
   DocumentViewerDialog
   ──────────────────────────────────────────────────────────────────
   Shared in-app overlay for previewing user-uploaded files
   (PDFs, images, and "open in new tab" fallback for anything else).

   Built in iter-76 because raw `target="_blank"` links to
   `/api/uploads/<file>.pdf` open the file in a chromeless browser tab
   on mobile Safari, with no obvious way to dismiss it. This wraps the
   file in a fullscreen overlay with:
     • Always-visible 48 px red X close in the top-right.
     • Esc-to-close keyboard shortcut.
     • Click-the-backdrop-to-close.
     • Open-in-new-tab, Download, and Print buttons for power users.

   Usage:
     <DocumentViewerDialog
       open={!!viewUrl}
       url={viewUrl}
       title="License — Health Department Permit"
       onClose={() => setViewUrl('')}
     />
   ────────────────────────────────────────────────────────────────── */

function isImage(url) {
  return /\.(png|jpe?g|gif|webp|svg|bmp|heic|heif|avif|tiff?)(\?|#|$)/i.test(url || '');
}

function isPdf(url) {
  return /\.pdf(\?|#|$)/i.test(url || '');
}

// iter-79: Office formats. We embed these via Microsoft's free Office
// Online viewer (https://view.officeapps.live.com/op/embed.aspx) which
// renders Word / Excel / PowerPoint inline in an iframe. The requirement
// is that the source URL is publicly reachable over HTTPS — our uploads
// route fits that since `/api/uploads/{filename}` is a public GET.
function isOffice(url) {
  return /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf)(\?|#|$)/i.test(url || '');
}

// iter-79: plain text formats we render inline by fetching the file and
// dumping it into a <pre>. Files past a sensible size are truncated so
// huge logs don't lock up the iframe.
function isText(url) {
  return /\.(txt|md|csv|log|json|xml|yaml|yml)(\?|#|$)/i.test(url || '');
}

const OFFICE_VIEWER = 'https://view.officeapps.live.com/op/embed.aspx?src=';

export default function DocumentViewerDialog({ open, url, title, onClose }) {
  // Esc-to-close. We attach the listener only while the dialog is open
  // so it doesn't intercept Esc in the rest of the app.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    // Body scroll lock so the underlying admin page can't scroll behind.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !url) return null;

  const kindImage = isImage(url);
  const kindPdf = isPdf(url);
  const kindOffice = isOffice(url);
  const kindText = isText(url);

  const stop = (e) => e.stopPropagation();

  return (
    <div
      data-testid="document-viewer-overlay"
      className="fixed inset-0 flex flex-col"
      style={{ background: 'rgba(10,26,47,0.96)', zIndex: 10001 }}
      onClick={onClose}
    >
      {/* Header bar — always present so the close affordance is
          obvious even when the file fills the viewport. */}
      <div
        onClick={stop}
        className="flex items-center justify-between gap-3 px-4 py-3 flex-shrink-0"
        style={{ background: 'rgba(10,26,47,0.85)', backdropFilter: 'blur(8px)' }}
      >
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}>
            Document preview
          </p>
          <p className="text-sm font-bold truncate" style={{ color: 'white', fontFamily: 'Manrope' }}>
            {title || 'Untitled document'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            data-testid="document-viewer-newtab"
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={stop}
            title="Open in new tab"
            aria-label="Open in new tab"
            className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer"
            style={{ touchAction: 'manipulation' }}
          >
            <ExternalLink className="w-4 h-4" strokeWidth={2.5} />
          </a>
          <a
            data-testid="document-viewer-download"
            href={url}
            download
            onClick={stop}
            title="Download"
            aria-label="Download document"
            className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer"
            style={{ touchAction: 'manipulation' }}
          >
            <Download className="w-4 h-4" strokeWidth={2.5} />
          </a>
          {kindImage && (
            <button
              data-testid="document-viewer-print"
              type="button"
              onClick={(e) => { stop(e); window.open(url, '_blank'); setTimeout(() => { try { window.print(); } catch { /* noop */ } }, 400); }}
              title="Print"
              aria-label="Print document"
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer"
              style={{ touchAction: 'manipulation' }}
            >
              <Printer className="w-4 h-4" strokeWidth={2.5} />
            </button>
          )}
          {kindText && (
            <button
              data-testid="document-viewer-polish"
              type="button"
              onClick={async (e) => {
                stop(e);
                const providerArgs = await getAiProviderArgs();
                if (providerArgs === null) return;
                try {
                  const r = await fetch(url, { credentials: 'include' });
                  if (!r.ok) throw new Error(`HTTP ${r.status}`);
                  const text = await r.text();
                  toast.message('Polishing — keeping the original safe.', { duration: 4000 });
                  const { data } = await axios.post(`${API}/admin/ai/polish-document`, {
                    raw_text: text.slice(0, 12000),
                    doc_type: 'general',
                    audience: 'ownership',
                    keep_original: true,
                    ...providerArgs,
                  }, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` } });
                  // Pop the polished markdown in a new tab so the user
                  // can review side-by-side with the original. Saving
                  // back to disk is left to the upload flows that
                  // brought the doc in — keeps this component pure.
                  const w = window.open('', '_blank');
                  if (w) {
                    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${(data.title || 'Atrium Polish').replace(/[<>]/g, '')}</title><style>body{font-family:Manrope,system-ui;max-width:760px;margin:40px auto;padding:0 24px;color:#0A1A2F;line-height:1.55}h1{color:#003DA5}blockquote{border-left:4px solid #003DA5;padding-left:16px;color:#4A5D5D;font-style:italic}pre{white-space:pre-wrap;background:#F8F5EC;padding:16px;border-radius:8px;font-family:ui-monospace,Menlo,monospace;font-size:13px}</style></head><body>`);
                    // Render markdown as best-effort pre (good enough for review).
                    w.document.write(`<pre>${(data.polished_markdown || '').replace(/[<>&]/g, (m) => ({'<':'&lt;','>':'&gt;','&':'&amp;'}[m]))}</pre>`);
                    w.document.write('</body></html>');
                    w.document.close();
                  }
                  toast.success('Atrium-polished version opened in a new tab.');
                } catch (err) {
                  toast.error(err?.response?.data?.detail || err?.message || 'Polish failed.');
                }
              }}
              title="Polish this document into Atrium format with AI"
              aria-label="Polish with AI"
              className="w-11 h-11 rounded-full text-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer"
              style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)', boxShadow: '0 4px 12px rgba(139,92,246,0.35)', touchAction: 'manipulation' }}
            >
              <Sparkles className="w-4 h-4" strokeWidth={2.5} />
            </button>
          )}
          {/* The big red X — keep it visually loud and tappable. */}
          <button
            data-testid="document-viewer-close"
            type="button"
            onClick={(e) => { stop(e); onClose?.(); }}
            title="Close (Esc)"
            aria-label="Close document"
            className="w-12 h-12 rounded-full bg-white text-slate-900 flex items-center justify-center shadow-lg ring-1 ring-black/10 hover:bg-red-50 hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer"
            style={{ touchAction: 'manipulation' }}
          >
            <X className="w-6 h-6" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Body — renders the file inline. PDFs via <iframe>, images via
          a centred <img>, anything else as a download/open prompt. */}
      <div className="flex-1 min-h-0 overflow-auto p-3 sm:p-6" onClick={stop}>
        {kindPdf && (
          /* iter-126 — Use <object> with a fallback chain so PDFs render
             in browsers that don't embed PDFs in iframes (mobile Safari,
             Brave, certain Chromium forks). The inner <embed> tries
             again; if THAT also fails, the user gets a visible "Open in
             new tab" link instead of a black screen. */
          <object
            data={`${url}#toolbar=1&navpanes=0`}
            type="application/pdf"
            className="w-full h-full bg-white rounded-lg"
            style={{ minHeight: '70vh' }}
            aria-label={title || 'PDF preview'}
            data-testid="document-viewer-pdf"
          >
            <embed src={`${url}#toolbar=1&navpanes=0`} type="application/pdf" className="w-full h-full" />
            <div className="w-full h-full flex items-center justify-center p-6 bg-white rounded-lg" style={{ minHeight: '70vh' }}>
              <div className="text-center" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>
                <p className="text-sm mb-3">Your browser couldn&apos;t embed the PDF inline.</p>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white"
                  style={{ background: '#1F4FBE' }}
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in a new tab
                </a>
              </div>
            </div>
          </object>
        )}
        {kindImage && (
          <div className="w-full h-full flex items-center justify-center">
            <img
              src={url}
              alt={title || 'Document'}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              style={{ minHeight: 0 }}
            />
          </div>
        )}
        {!kindPdf && !kindImage && kindOffice && (
          <iframe
            src={`${OFFICE_VIEWER}${encodeURIComponent(url)}`}
            title={title || 'Document'}
            className="w-full h-full bg-white rounded-lg"
            style={{ minHeight: '70vh', border: 'none' }}
            data-testid="document-viewer-office"
          />
        )}
        {!kindPdf && !kindImage && !kindOffice && kindText && (
          <TextPreview url={url} title={title} />
        )}
        {!kindPdf && !kindImage && !kindOffice && !kindText && (
          <div className="w-full h-full flex items-center justify-center">
            {/* iter-126 — Solid white card (was rgba(255,255,255,0.06)
                which rendered as a near-invisible black panel on the
                dark backdrop — the "black screen" the user reported). */}
            <div className="max-w-md text-center p-8 rounded-xl bg-white shadow-xl" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
              <p className="text-sm mb-4">This file type can&apos;t be previewed inline.</p>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold"
                style={{ backgroundColor: '#1F4FBE', color: 'white' }}
                data-testid="document-viewer-open-newtab"
              >
                <ExternalLink className="w-4 h-4" />
                Open in a new tab
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// iter-79: lightweight inline renderer for plain-text files (.txt, .md,
// .csv, .json, .log). Fetches the body once, caps at 200KB so very
// large logs don't freeze the iframe, and renders in a monospace pre.
function TextPreview({ url, title }) {
  const [state, setState] = useState({ loading: true, text: '', error: '' });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, text: '', error: '' });
    fetch(url, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        // Read up to 200KB worth of text. We use the ArrayBuffer path
        // because that lets us slice on byte boundaries even with
        // multibyte UTF-8.
        const buf = await r.arrayBuffer();
        const slice = buf.byteLength > 200 * 1024 ? buf.slice(0, 200 * 1024) : buf;
        const text = new TextDecoder('utf-8').decode(slice);
        const truncated = buf.byteLength > 200 * 1024 ? `${text}\n\n…(truncated — file is ${(buf.byteLength / 1024).toFixed(0)} KB)` : text;
        if (!cancelled) setState({ loading: false, text: truncated, error: '' });
      })
      .catch((e) => { if (!cancelled) setState({ loading: false, text: '', error: e.message || 'Failed to load file.' }); });
    return () => { cancelled = true; };
  }, [url]);

  if (state.loading) {
    return (
      <div className="w-full h-full flex items-center justify-center" data-testid="document-viewer-text-loading">
        <Loader2 className="w-6 h-6 animate-spin text-white" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="max-w-md text-center p-8 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'white', fontFamily: 'Manrope' }}>
          <p className="text-sm">Could not load file: {state.error}</p>
        </div>
      </div>
    );
  }
  return (
    <pre
      data-testid="document-viewer-text"
      className="w-full h-full bg-white rounded-lg p-4 text-[12px] leading-relaxed overflow-auto"
      style={{ minHeight: '70vh', fontFamily: 'ui-monospace, Menlo, monospace', color: '#1A2E2E', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
    >{state.text}</pre>
  );
}
